import { Api } from '@core/types';
import { Dispatch } from 'redux';
import { FETCH_REPORTS, FETCH_REPORTS_ERROR, FETCH_REPORTS_SUCCESS } from './actions';
import { ApiService } from '@core/services';

export const fetchReports = (params: Api.IFetchReportsRequest) => async (dispatch: Dispatch) => {
  // Initial action to trigger loaders

  dispatch({
    type: FETCH_REPORTS,
  });

  const response = await ApiService.request<Api.IFetchReportsResponse>({
    controller: 'reporting',
    method: Api.Method.GET,
    slug: 'generate-reports',
    bearerToken: params.bearerToken,
    params: {
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      templateNames: params.templateNames,
    },
  });

  if (!response.success || response.errors.length > 0 || !response.data) {
    return dispatch({
      type: FETCH_REPORTS_ERROR,
      payload: response.errors,
    });
  }

  // dispatch the final response data
  return dispatch({
    type: FETCH_REPORTS_SUCCESS,
    payload: {
      printsByUser: response.data.printsByUser,
      printsByCulture: response.data.printsByCulture,
      downloadsByUser: response.data.downloadsByUser,
      downloadsByCulture: response.data.downloadsByCulture,
      downloadsByTemplate: response.data.downloadsByTemplate,
      printsByTemplate: response.data.printsByTemplate,
    },
  });
};

export const downloadPrintsByTemplateAndCultureCsv =
  (params: { bearerToken: string; dateFrom?: string; dateTo?: string; templateNames?: string[] }) => async (dispatch: Dispatch) => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (params.dateFrom) {
        queryParams.append('dateFrom', params.dateFrom);
      }
      if (params.dateTo) {
        queryParams.append('dateTo', params.dateTo);
      }

      if (params.templateNames && params.templateNames.length > 0) {
        params.templateNames.forEach((template) => {
          queryParams.append('templateNames', template);
        });
      }

      const baseUrl = process.env.REACT_APP_API_ENDPOINT;
      const url = `${baseUrl}/api/reporting/prints-by-template-culture?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${params.bearerToken}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `prints-by-template-culture-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        console.error('Failed to download CSV:', response.statusText);
        return dispatch({
          type: FETCH_REPORTS_ERROR,
          payload: [{ fieldName: 'download', message: `Failed to download CSV: ${response.statusText}` }],
        });
      }
    } catch (error) {
      console.error('Error downloading CSV:', error);
      return dispatch({
        type: FETCH_REPORTS_ERROR,
        payload: [{ fieldName: 'download', message: 'Error downloading CSV' }],
      });
    }
  };

export const downloadDownloadsByTemplateAndCultureCsv =
  (params: { bearerToken: string; dateFrom?: string; dateTo?: string; templateNames?: string[] }) => async (dispatch: Dispatch) => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (params.dateFrom) {
        queryParams.append('dateFrom', params.dateFrom);
      }
      if (params.dateTo) {
        queryParams.append('dateTo', params.dateTo);
      }

      if (params.templateNames && params.templateNames.length > 0) {
        params.templateNames.forEach((template) => {
          queryParams.append('templateNames', template);
        });
      }

      const baseUrl = process.env.REACT_APP_API_ENDPOINT;
      const url = `${baseUrl}/api/reporting/downloads-by-template-culture?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${params.bearerToken}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `downloads-by-template-culture-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        console.error('Failed to download CSV:', response.statusText);
        return dispatch({
          type: FETCH_REPORTS_ERROR,
          payload: [{ fieldName: 'download', message: `Failed to download CSV: ${response.statusText}` }],
        });
      }
    } catch (error) {
      console.error('Error downloading CSV:', error);
      return dispatch({
        type: FETCH_REPORTS_ERROR,
        payload: [{ fieldName: 'download', message: 'Error downloading CSV' }],
      });
    }
  };
